import type { Scene, WebGLRenderer } from 'three';

export interface RenderAccess {
  renderer: WebGLRenderer;
  scene: Scene;
}

export interface ZoneEvent {
  zoneId: string;
}

export interface ActivityEvent {
  activityId: string;
  zoneId: string;
}

export interface ActivityEndEvent extends ActivityEvent {
  status: 'completed' | 'cancelled' | 'failed';
}

export type Tier = 'low' | 'mid' | 'high';
export type Quality = 'auto' | Tier;

export interface GameEvents {
  'zone:enter': ZoneEvent;
  'zone:exit': ZoneEvent;
  'activity:start': ActivityEvent;
  'activity:end': ActivityEndEvent;
  'save:changed': undefined;
  'audio:mute': { muted: boolean };
  'tier:changed': { tier: Tier; reason: 'detect' | 'measure' | 'user' };
  'quality:set': { quality: Quality };
  'ui:enter': undefined;
  'ui:skip-intro': undefined;
  'ui:name-done': undefined;
  'ui:action': { activityId: string };
  'ui:activity-close': undefined;
  'tutorial:skip': undefined;
  'tutorial:replay': undefined;
  'input:action': undefined;
  'render:borrow': { use: (access: RenderAccess) => void };
}

type Handler<T> = (payload: T) => void;

export class EventBus<Events extends object = GameEvents> {
  private handlers = new Map<keyof Events, Set<Handler<never>>>();

  on<K extends keyof Events>(type: K, handler: Handler<Events[K]>): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    set.add(handler as Handler<never>);
    return () => this.off(type, handler);
  }

  once<K extends keyof Events>(type: K, handler: Handler<Events[K]>): () => void {
    const off = this.on(type, (payload) => {
      off();
      handler(payload);
    });
    return off;
  }

  off<K extends keyof Events>(type: K, handler: Handler<Events[K]>): void {
    this.handlers.get(type)?.delete(handler as Handler<never>);
  }

  emit<K extends keyof Events>(
    type: K,
    ...args: Events[K] extends undefined ? [] : [Events[K]]
  ): void {
    const set = this.handlers.get(type);
    if (!set) return;
    for (const handler of [...set]) (handler as Handler<Events[K] | undefined>)(args[0]);
  }

  clear(): void {
    this.handlers.clear();
  }
}
